import React from 'react';

type LanguageCode =
  | 'ru-RU'
  | 'be-BY'
  | 'kk-KZ'
  | 'uz-UZ'
  | 'tg-TJ'
  | 'ky-KG'
  | 'en-EN'
  | 'es-ES'
  | 'de-DE'
  | 'fr-FR'
  | 'pl-PL';

interface LanguageFlagProps {
  language: string;
  size?: number;
}

function getFlagStyles(language: string): {
  background: string;
  children?: React.ReactNode;
} {
  const code = language as LanguageCode;

  switch (code) {
    case 'ru-RU':
      return {
        background: 'linear-gradient(180deg, #ffffff 0 33.33%, #2458a6 33.33% 66.66%, #d52b1e 66.66% 100%)',
      };
    case 'be-BY':
      return {
        background: 'linear-gradient(180deg, #c8313e 0 78%, #2e8b57 78% 100%)',
        children: (
          <span
            style={{
              position: 'absolute',
              inset: '0 auto 0 0',
              width: '22%',
              background:
                'repeating-linear-gradient(180deg, #ffffff 0 3px, #c8313e 3px 6px, #ffffff 6px 9px)',
              borderRight: '1px solid rgba(255,255,255,0.45)',
            }}
          />
        ),
      };
    case 'kk-KZ':
      return {
        background: '#3db7d6',
        children: (
          <>
            <span
              style={{
                position: 'absolute',
                left: '14%',
                top: '14%',
                bottom: '14%',
                width: '10%',
                borderRadius: '999px',
                background:
                  'repeating-linear-gradient(180deg, #f6c445 0 2px, transparent 2px 4px)',
                opacity: 0.95,
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '26%',
                height: '26%',
                borderRadius: '999px',
                background: '#f6c445',
                top: '37%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 0 3px rgba(246,196,69,0.22)',
              }}
            />
          </>
        ),
      };
    case 'uz-UZ':
      return {
        background:
          'linear-gradient(180deg, #1eb5e8 0 32%, #d81e2d 32% 36%, #ffffff 36% 64%, #d81e2d 64% 68%, #1faa59 68% 100%)',
        children: (
          <>
            <span
              style={{
                position: 'absolute',
                top: '11%',
                left: '8%',
                width: '18%',
                height: '24%',
                borderRadius: '999px',
                boxShadow: 'inset -3px 0 0 0 #ffffff',
                background: '#ffffff',
                opacity: 0.95,
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: '15%',
                left: '25%',
                width: '4%',
                height: '4%',
                borderRadius: '999px',
                background: '#ffffff',
                boxShadow: '8px 0 0 #ffffff, 16px 0 0 #ffffff, 4px 7px 0 #ffffff, 12px 7px 0 #ffffff, 20px 7px 0 #ffffff',
              }}
            />
          </>
        ),
      };
    case 'tg-TJ':
      return {
        background: 'linear-gradient(180deg, #ce1126 0 33.33%, #ffffff 33.33% 66.66%, #239f40 66.66% 100%)',
        children: (
          <span
            style={{
              position: 'absolute',
              left: '50%',
              top: '45%',
              transform: 'translate(-50%, -50%)',
              width: '18%',
              height: '12%',
              borderRadius: '0 0 999px 999px',
              border: '2px solid #f0c419',
              borderTop: '0',
            }}
          />
        ),
      };
    case 'ky-KG':
      return {
        background: '#d91f26',
        children: (
          <>
            <span
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: '28%',
                height: '28%',
                transform: 'translate(-50%, -50%)',
                borderRadius: '999px',
                boxShadow: 'inset 0 0 0 2px #f6c445',
              }}
            />
            <span
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: '36%',
                height: '2px',
                transform: 'translate(-50%, -50%)',
                background: '#f6c445',
                boxShadow: '0 -6px 0 #f6c445, 0 6px 0 #f6c445',
              }}
            />
            <span
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: '2px',
                height: '36%',
                transform: 'translate(-50%, -50%)',
                background: '#f6c445',
                boxShadow: '-6px 0 0 #f6c445, 6px 0 0 #f6c445',
              }}
            />
          </>
        ),
      };
    case 'en-EN':
      return {
        background:
          'repeating-linear-gradient(180deg, #b22234 0 7.69%, #ffffff 7.69% 15.38%)',
        children: (
          <span
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '48%',
              height: '54%',
              background:
                'radial-gradient(circle at 22% 24%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 50% 24%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 78% 24%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 36% 46%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 64% 46%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 22% 68%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 50% 68%, rgba(255,255,255,0.95) 0 7%, transparent 8%), radial-gradient(circle at 78% 68%, rgba(255,255,255,0.95) 0 7%, transparent 8%), #3c3b6e',
            }}
          />
        ),
      };
    case 'es-ES':
      return {
        background: 'linear-gradient(180deg, #aa151b 0 25%, #f1bf00 25% 75%, #aa151b 75% 100%)',
      };
    case 'de-DE':
      return {
        background: 'linear-gradient(180deg, #000000 0 33.33%, #dd0000 33.33% 66.66%, #ffce00 66.66% 100%)',
      };
    case 'fr-FR':
      return {
        background: 'linear-gradient(90deg, #0055a4 0 33.33%, #ffffff 33.33% 66.66%, #ef4135 66.66% 100%)',
      };
    case 'pl-PL':
      return {
        background: 'linear-gradient(180deg, #ffffff 0 50%, #dc143c 50% 100%)',
      };
    default:
      return {
        background: 'linear-gradient(135deg, #dfe7f4 0%, #aebfd6 100%)',
      };
  }
}

export const LanguageFlag: React.FC<LanguageFlagProps> = ({ language, size = 18 }) => {
  const { background, children } = getFlagStyles(language);
  const height = size;
  const width = Math.round(size * 1.4);

  return (
    <span
      aria-hidden="true"
      style={{
        width,
        height,
        minWidth: width,
        display: 'inline-flex',
        position: 'relative',
        borderRadius: Math.max(4, Math.round(height * 0.22)),
        overflow: 'hidden',
        background,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35), 0 1px 2px rgba(0,0,0,0.18)',
        flexShrink: 0,
        verticalAlign: 'middle',
      }}
    >
      {children}
    </span>
  );
};

export default LanguageFlag;
